/**
 * Tariff Hydration Utilities
 *
 * Functions for loading and hydrating tariff data from various sources
 * when loading saved scenarios.
 *
 * @module utils/tariffHydration
 */

import { debug } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';

/**
 * Hydrate electric tariff data from saved scenario
 *
 * Handles three cases:
 * 1. tariff_label exists but tariff_data is null - fetch from Flux Rate Database
 * 2. tariff_data exists - use it directly
 * 3. Neither exists - no tariff to hydrate
 *
 * @param {Object} options
 * @param {Object} options.data - Loaded scenario data with tariff_label and tariff_data
 * @param {Object} options.headers - Auth headers for API requests
 * @param {Function} options.setSelectedTariffData - Function to set selected tariff data
 * @param {Function} options.setCustomTariffData - Function to set custom tariff data
 * @param {Function} options.setVortxSettings - Function to update vortx settings for TOU display
 */
export async function hydrateElectricTariff({
    data,
    headers,
    setSelectedTariffData,
    setCustomTariffData,
    setVortxSettings,
}) {
    // Case 1: tariff_label exists but tariff_data is null - fetch from Flux Rate Database
    if (data.tariff_label && !data.tariff_data) {
        debug.log("Hydrating tariff from Flux Rate Database:", data.tariff_label);
        try {
            // Try user's custom rates first
            let tariffRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/custom-rates/${data.tariff_label}`, { headers });

            // If not found, try global approved tariffs via admin rates endpoint
            if (tariffRes.status === 404) {
                debug.log("Not found in custom-rates, trying admin/rates...");
                // Get list of approved rates and find by ID
                const adminRatesRes = await fetch(`${API_BASE_URL}/api/v1/admin/tariffs`, { headers });
                if (adminRatesRes.ok) {
                    const adminData = await adminRatesRes.json();
                    const matchingRate = adminData.rates?.find(r => r.id === data.tariff_label);
                    if (matchingRate) {
                        debug.log("Hydrated tariff from approved_tariffs:", matchingRate);
                        setSelectedTariffData(matchingRate);
                        if (matchingRate.rate_data) {
                            setCustomTariffData(matchingRate.rate_data);
                        }
                    } else {
                        debug.error("❌ Tariff not found! The referenced tariff was deleted. Looking for:", data.tariff_label);
                        debug.log("Available rate IDs:", adminData.rates?.map(r => r.id));
                        debug.error("❌ User must select a new tariff from the Tariff tab before financial calculations will work.");
                    }
                } else {
                    debug.warn("Failed to fetch admin rates:", adminRatesRes.status);
                }
            } else if (tariffRes.ok) {
                const tariffData = await tariffRes.json();
                debug.log("Hydrated tariff data from custom-rates:", tariffData);
                setSelectedTariffData(tariffData);
                setCustomTariffData(tariffData);
            } else {
                debug.warn("Failed to hydrate tariff:", tariffRes.status);
            }
        } catch (err) {
            debug.error("Error hydrating tariff:", err);
        }
    }
    // Case 2: Direct tariff data available - use it
    else if (data.tariff_data) {
        debug.log("Loading saved tariff_data:", data.tariff_data);

        // Ensure the tariff_data has an ID (carry over from tariff_label if missing)
        const tariffWithId = {
            ...data.tariff_data,
            id: data.tariff_data.id || data.tariff_label
        };

        setSelectedTariffData(tariffWithId);
        // Also set customTariffData for CustomRateBuilder to display properly
        setCustomTariffData(tariffWithId);

        // IMPORTANT: Also hydrate vortxSettings to display TOU data correctly
        const rateData = tariffWithId.rate_data || {};
        if (rateData.touData && setVortxSettings) {
            setVortxSettings(prev => ({
                ...prev,
                rateMode: tariffWithId.rate_mode || 'tou',
                touData: rateData.touData
            }));
            debug.log("Hydrated vortxSettings.touData:", rateData.touData);
        }
    }
}

/**
 * Hydrate gas tariff data from saved scenario
 *
 * Handles two cases:
 * 1. gas_tariff_data exists - use it directly
 * 2. gas_tariff_id exists but no data - fetch from backend
 *
 * @param {Object} options
 * @param {Object} options.data - Loaded scenario data with gas_tariff_id and gas_tariff_data
 * @param {Object} options.headers - Auth headers for API requests
 * @param {Function} options.setGasTariffData - Function to set gas tariff data
 * @param {Function} options.setGasTariffId - Function to set gas tariff ID
 * @param {Function} options.setGeneratorFuelCostPerMmbtu - Function to sync fuel cost
 * @param {Function} options.convertGasTariffForSolver - Converter function for gas tariff
 */
export async function hydrateGasTariff({
    data,
    headers,
    setGasTariffData,
    setGasTariffId,
    setGeneratorFuelCostPerMmbtu,
    convertGasTariffForSolver,
}) {
    if (!data.gas_tariff_id && !data.gas_tariff_data) {
        return; // No gas tariff to hydrate
    }

    debug.log("Loading gas tariff:", {
        gas_tariff_id: data.gas_tariff_id,
        has_gas_tariff_data: !!data.gas_tariff_data
    });

    // Helper to sync fuel cost from gas tariff using existing converter
    const syncFuelCostFromTariff = (tariffData) => {
        if (!convertGasTariffForSolver || !setGeneratorFuelCostPerMmbtu) return;

        try {
            const converted = convertGasTariffForSolver(tariffData);
            if (converted?.tiers?.length > 0) {
                const ratePerMmbtu = converted.tiers[0]?.rate_per_mmbtu;
                if (ratePerMmbtu && ratePerMmbtu > 0) {
                    debug.log(`📊 Syncing fuel cost from loaded gas tariff: $${ratePerMmbtu.toFixed(2)}/MMBtu`);
                    setGeneratorFuelCostPerMmbtu(ratePerMmbtu.toFixed(2));
                }
            }
        } catch (err) {
            debug.warn('Failed to convert gas tariff:', err);
        }
    };

    if (data.gas_tariff_data) {
        // Direct gas tariff data available - use it
        setGasTariffData(data.gas_tariff_data);
        setGasTariffId(data.gas_tariff_id || data.gas_tariff_data.id);
        syncFuelCostFromTariff(data.gas_tariff_data);
    } else if (data.gas_tariff_id) {
        // Only ID available - fetch from backend
        try {
            const tariffRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/custom-rates/${data.gas_tariff_id}`, { headers });
            if (tariffRes.ok) {
                const tariffData = await tariffRes.json();
                debug.log("Hydrated gas tariff data:", tariffData);
                setGasTariffData(tariffData);
                setGasTariffId(data.gas_tariff_id);
                syncFuelCostFromTariff(tariffData);
            } else {
                debug.warn("Failed to hydrate gas tariff:", tariffRes.status);
            }
        } catch (err) {
            debug.error("Error hydrating gas tariff:", err);
        }
    }
}
